import React, { useState } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Box, Typography, CircularProgress, IconButton, Tooltip } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DOMPurify from 'dompurify';

const ExpandableDataGrid = ({ 
    columns, 
    rows, 
    columnsWithActions,
    height = 400,
    expandableFields = [], // Array of field names that should be expandable
    ...props 
}) => {
    const [expandedCells, setExpandedCells] = useState({});

    const toggleCellExpansion = (id, field) => {
        setExpandedCells(prev => ({
            ...prev,
            [`${id}-${field}`]: !prev[`${id}-${field}`]
        }));
    };

    const renderHTML = (content) => {
        if (!content || content === '-') return '-';
        const sanitizedContent = DOMPurify.sanitize(content);
        return <div dangerouslySetInnerHTML={{ __html: sanitizedContent }} />;
    };

    const customTheme = createTheme({
        components: {
            MuiDataGrid: {
                styleOverrides: {
                    root: {
                        border: 'none',
                        '& .MuiDataGrid-main': {
                            '&:focus-within': {
                                outline: 'none',
                            },
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#f8fafc',
                            borderBottom: '1px solid rgba(224, 224, 224, 0.4)',
                            '& .MuiDataGrid-columnSeparator': {
                                display: 'none',
                            },
                        },
                        '& .MuiDataGrid-columnHeader': {
                            color: 'rgba(0, 0, 0, 0.7)',
                            fontWeight: '600',
                            fontSize: '0.875rem',
                            padding: '0 16px',
                            height: '56px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                            '&:focus': {
                                outline: 'none',
                            },
                        },
                        '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid rgba(224, 224, 224, 0.3)',
                            fontSize: '0.875rem',
                            padding: '16px',
                            '&:focus': {
                                outline: 'none',
                            },
                            '&:focus-within': {
                                outline: 'none',
                            },
                            '&.expandable-cell': {
                                whiteSpace: 'normal',
                                lineHeight: '1.5',
                                minHeight: '52px !important',
                            },
                            '& .rich-text-content': {
                                '& p': { margin: 0 },
                                '& ul, & ol': { margin: '0.5em 0', paddingLeft: '1.5em' },
                                '& img': { maxWidth: '100%', height: 'auto' },
                            },
                        },
                        '& .MuiDataGrid-row': {
                            '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                            },
                            '&.Mui-selected': {
                                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.12)',
                                },
                            },
                        },
                        '& .cell-content': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            '-webkit-box-orient': 'vertical',
                            transition: 'all 0.3s ease',
                        },
                        '& .cell-content.collapsed': {
                            '-webkit-line-clamp': 2,
                            maxHeight: '3em',
                        },
                        '& .cell-content.expanded': {
                            '-webkit-line-clamp': 'unset',
                            maxHeight: 'none',
                        },
                    },
                },
            },
        },
    });

    const enhancedColumns = (columnsWithActions || columns).map(column => {
        if (expandableFields.includes(column.field)) {
            return {
                ...column,
                renderCell: (params) => {
                    const cellId = `${params.id}-${column.field}`;
                    const isExpanded = expandedCells[cellId];
                    const content = params.value || '';

                    return (
                        <Box sx={{ 
                            width: '100%',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                            py: 1
                        }}>
                            <Box
                                className={`cell-content rich-text-content ${isExpanded ? 'expanded' : 'collapsed'}`}
                                sx={{ flex: 1 }}
                            >
                                {renderHTML(content)}
                            </Box>
                            {content.length > 100 && (
                                <IconButton
                                    size="small"
                                    onClick={() => toggleCellExpansion(params.id, column.field)}
                                    sx={{ 
                                        mt: -0.5,
                                        '&:hover': {
                                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                        }
                                    }}
                                >
                                    <Tooltip title={isExpanded ? "Show less" : "Show more"}>
                                        {isExpanded ? 
                                            <KeyboardArrowUpIcon fontSize="small" /> : 
                                            <KeyboardArrowDownIcon fontSize="small" />
                                        }
                                    </Tooltip>
                                </IconButton>
                            )}
                        </Box>
                    );
                },
                sortComparator: (v1, v2) => {
                    // Remove HTML tags for sorting
                    const stripHtml = (html) => {
                        const tmp = document.createElement('div');
                        tmp.innerHTML = html;
                        return tmp.textContent || tmp.innerText || '';
                    };
                    return stripHtml(v1).localeCompare(stripHtml(v2));
                },
            };
        }
        return column;
    });

    return (
        <ThemeProvider theme={customTheme}>
            <Box 
                sx={{ 
                    height: height, 
                    width: '100%', 
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                }}
            >
                <DataGrid
                    rows={rows || []}
                    columns={enhancedColumns}
                    components={{
                        Toolbar: GridToolbar,
                        LoadingOverlay: () => (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    bottom: 0,
                                    right: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        ),
                        NoRowsOverlay: () => (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                    p: 3,
                                }}
                            >
                                <Typography variant="body1" color="text.secondary" align="center">
                                    No data available
                                </Typography>
                                <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                                    Try adjusting your filters or search criteria
                                </Typography>
                            </Box>
                        ),
                    }}
                    componentsProps={{
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 500 },
                            sx: {
                                p: 2,
                                backgroundColor: '#f8fafc',
                                borderBottom: '1px solid rgba(224, 224, 224, 0.4)',
                                '& .MuiButton-root': {
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    padding: '6px 12px',
                                },
                            },
                        },
                    }}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 25,
                            },
                        },
                    }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    getRowHeight={() => 'auto'}
                    disableColumnMenu={false}
                    disableDensitySelector={false}
                    disableColumnSelector={false}
                    disableColumnFilter={false}
                    sx={{
                        '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
                            outline: 'none',
                        },
                        '& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus': {
                            outline: 'none',
                        }
                    }}
                    {...props}
                />
            </Box>
        </ThemeProvider>
    );
};

export default ExpandableDataGrid; 